import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Instagram, Play, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/Reveal";
import { GreekKeyDivider } from "@/components/GreekKeyDivider";
import { GreekWatermark } from "@/components/GreekWatermark";
import { Marquee } from "@/components/Marquee";
import { ElysiumStandard } from "@/components/home/ElysiumStandard";
import { IsItForMe } from "@/components/home/IsItForMe";
import { MemberStories } from "@/components/home/MemberStories";
import { FAQSection } from "@/components/home/FAQSection";
import { PlanYourWeek } from "@/components/home/PlanYourWeek";
import { useBooking } from "@/components/booking/BookingDialog";

const HERO_VIDEO = "/videos/gym-training-loop.mp4";
const HERO_POSTER = "/videos/gym-training-loop-poster.jpg";
// Phones get the new vertical highlights film, which fills a phone screen; wide screens keep the landscape loop.
const PHONE_VIDEO = "/videos/elysium-highlights.mp4";
const PHONE_POSTER = "/videos/elysium-highlights-poster.jpg";
const CLASSES_IMG = "/gallery/training-hall.jpg";
const SIGN_IMG = "/gallery/elysium-sign.jpg";

const INSTAGRAM_URL = "https://www.instagram.com/elysiumtrainingclub";

const COMMUNITY_SHOTS = [
  { type: "image", img: "/gallery/coaches.jpg", ratio: "aspect-[4/5]" },
  { type: "video", video: PHONE_VIDEO, poster: PHONE_POSTER, ratio: "aspect-[9/16]" },
  { type: "image", img: "/gallery/elysium-sign.jpg", ratio: "aspect-square" },
  { type: "image", img: "/gallery/front-squat.jpg", ratio: "aspect-[4/5]" },
];


export default function Home() {
  const { open } = useBooking();
  return (
    <div>
      <section className="relative min-h-[92vh] flex items-end sm:items-center overflow-hidden">
        <div className="absolute top-0 inset-x-0 h-3 sm:h-5 bg-void z-20" />
        <div className="absolute bottom-0 inset-x-0 h-3 sm:h-5 bg-void z-20" />
        <motion.div
          className="absolute inset-0"
          initial={{ scale: 1.15, x: 0, y: 0 }}
          animate={{ scale: 1, x: -10, y: -6 }}
          transition={{ duration: 14, ease: [0.16, 1, 0.3, 1] }}
        >
          <video src={PHONE_VIDEO} poster={PHONE_POSTER} autoPlay muted loop playsInline preload="metadata"
            className="sm:hidden w-full h-full object-cover" data-testid="hero-video-phone" />
          <video src={HERO_VIDEO} poster={HERO_POSTER} autoPlay muted loop playsInline preload="metadata"
            className="hidden sm:block w-full h-full object-cover object-[50%_20%]" data-testid="hero-video" />
          <div className="absolute inset-0 bg-gradient-to-t from-void/95 via-void/40 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-void/55 via-transparent to-void/35" />
          <div className="absolute inset-0" style={{ boxShadow: "inset 0 0 220px 60px rgba(7,6,4,0.85)" }} />
        </motion.div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 sm:pb-0 pt-32 sm:pt-0 w-full">
          <p className="eyebrow" data-testid="hero-tagline">Coaching-led fitness · Swansea</p>
          <h1
            className="font-anton uppercase tracking-tight text-[2.6rem] leading-[0.95] sm:text-6xl lg:text-7xl max-w-4xl mt-4 text-balance bg-gradient-to-b from-[#f6ecd2] via-gold to-[#8a7a58] bg-clip-text text-transparent drop-shadow-[0_4px_30px_rgba(154,137,104,0.35)]"
            data-testid="hero-heading"
          >
            Swansea&apos;s coaching-led functional fitness gym
          </h1>
          <p className="text-cream/90 text-base sm:text-lg mt-6 max-w-xl leading-relaxed">
            Expert coaching. Structured programming. A community built to help you become stronger, fitter and more confident.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 mt-9">
            <Button size="lg" onClick={() => open()} className="rounded-none bg-gold text-void hover:bg-gold-hover font-bold uppercase tracking-wider h-14 px-8" data-testid="hero-book-class-button">
              Book Your First Free Coached Session <ArrowRight className="h-4 w-4" />
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-none border-gold text-gold hover:bg-gold hover:text-void h-14 px-8 bg-transparent" data-testid="hero-view-timetable-button">
              <a href="#timetable">View Timetable</a>
            </Button>
          </div>
        </div>
        <a href="#standard" className="absolute bottom-8 sm:bottom-10 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-gold" data-testid="hero-scroll-cue">
          <span className="text-[12px] sm:text-[13px] uppercase tracking-[0.3em] font-semibold whitespace-nowrap">Train with purpose in Swansea</span>
          <ChevronDown className="h-5 w-5 animate-bounce" />
        </a>
        <GreekKeyDivider className="absolute bottom-0 left-0 z-10" />
      </section>

      <div className="mt-10 sm:mt-14">
        <Marquee bgImage={SIGN_IMG} />
      </div>

      <ElysiumStandard />

      <IsItForMe />

      <PlanYourWeek />

      <GreekKeyDivider />

      <section className="py-24 sm:py-32 relative">
        <GreekWatermark letter="Σ" className="top-0 left-0 sm:left-10" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <Reveal className="text-center max-w-2xl mx-auto">
            <p className="eyebrow mb-3">Straight from the floor</p>
            <h2 className="font-anton uppercase tracking-tighter text-cream text-4xl sm:text-5xl">Follow the journey</h2>
            <p className="text-smoke mt-4">Real sessions and real members, straight from our Instagram, @elysiumtrainingclub.</p>
          </Reveal>

          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noreferrer"
            className="md:hidden mt-8 flex items-center justify-center gap-2 border border-gold/40 bg-surface px-5 py-3 hover:border-gold transition-colors"
            data-testid="home-instagram-follow-link-mobile"
          >
            <Instagram className="h-4 w-4 text-gold" />
            <span className="text-gold uppercase tracking-wider text-sm">Follow @elysiumtrainingclub</span>
          </a>

          <div className="relative mt-14">
            <div className="columns-2 sm:columns-3 gap-5 sm:gap-6 [column-fill:balance]">
              {COMMUNITY_SHOTS.map((shot, i) => (
                <Reveal key={shot.img || shot.video} delay={i * 0.08} className="mb-5 sm:mb-6 break-inside-avoid">
                  <a
                    href={INSTAGRAM_URL}
                    target="_blank"
                    rel="noreferrer"
                    className={`group relative block w-full overflow-hidden border border-line hover:border-gold/50 transition-colors ${shot.ratio}`}
                    data-testid={`home-community-shot-${i}`}
                  >
                    {shot.type === "video" ? (
                      <video
                        src={shot.video}
                        poster={shot.poster}
                        autoPlay
                        muted
                        loop
                        playsInline
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    ) : (
                      <img src={shot.img} alt="Elysium community" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    )}
                    <div className="absolute top-3 right-3 h-9 w-9 rounded-full bg-void/60 border border-gold/40 flex items-center justify-center backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity">
                      {shot.type === "video" ? <Play className="h-3.5 w-3.5 text-gold fill-gold" /> : <Instagram className="h-3.5 w-3.5 text-gold" />}
                    </div>
                  </a>
                </Reveal>
              ))}
            </div>

            <div className="hidden md:block absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
              <Reveal delay={0.3} className="bg-void/85 backdrop-blur-2xl border border-gold/40 px-8 py-7 text-center" style={{ boxShadow: "0 0 60px 20px rgba(7,6,4,0.7)" }}>
                <Play className="h-5 w-5 text-gold fill-gold mx-auto mb-3" />
                <a
                  href={INSTAGRAM_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 text-gold uppercase tracking-wider text-sm hover:gap-3 transition-all"
                  data-testid="home-instagram-follow-link"
                >
                  Follow the Journey <ArrowRight className="h-4 w-4" />
                </a>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <MemberStories />

      <FAQSection />

      <section className="relative py-28 sm:py-36 overflow-hidden">
        <div className="absolute inset-0">
          <img src={CLASSES_IMG} alt="Weights" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-void/70" />
          <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at center, rgba(154,137,104,0.18), transparent 65%)" }} />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <Reveal>
            <p className="eyebrow mb-3">Your first step</p>
            <h2 className="font-anton uppercase tracking-tighter text-cream text-4xl sm:text-6xl">
              Ready to start?
            </h2>
            <p className="text-smoke mt-6 text-base sm:text-lg max-w-xl mx-auto">
              Book your first free coached session today and experience Elysium for yourself.
            </p>
            <Button size="lg" onClick={() => open()} className="rounded-none bg-gold text-void hover:bg-gold-hover font-bold uppercase tracking-wider h-14 px-6 sm:px-10 mt-9 whitespace-normal" data-testid="home-final-cta-button">
              Book Your First Free Coached Session
            </Button>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
